/**
 * User-facing copy for capture mode and the issue rows it produces, shared by
 * the two surfaces that render the same states to the same person: the
 * extension's annotation canvas (content/pins/CanvasToolbar.tsx, PinListItem
 * .tsx, IssueTableRow.tsx) and the team-review drawer
 * (review-app/components/CanvasToolbar.tsx, issue-table/IssueRow.tsx).
 *
 * It lives here because those packages cannot see each other. Each used to
 * hold its own literal, so a wording change in one diverged silently from the
 * other. `packages/shared/src/__tests__/captureCopy.test.ts` fails if any
 * consumer reintroduces an inline copy or resurrects a retired string.
 *
 * No em dashes (project copy rule); the test pins that too.
 */
/**
 * The armed capture toolbar. Rewritten 2026-08-14 from the product-psychology
 * teardown measured against BugHerd on the same fixture
 * (docs/test-reports/2026-08-13-issue-creation-vs-bugherd/).
 *
 * The finding: our capture mode blocks the page exactly as hard as BugHerd's
 * (pointer-events:auto, max z-index, a real click does not land) but PERSISTS
 * until Done where theirs ends after one click, and nothing in the armed state
 * said the session survives leaving it. Pastel and Ruttl both name a
 * destination you return from ("Browse mode"); the old copy named a door out.
 */
export const CAPTURE_MODE_COPY = {
    /**
     * What the armed canvas is for. "capture it" describes the gesture; the old
     * "create issue" described a database row the user has not asked for yet.
     */
    selectInstruction: 'Click any element to capture it',
    /**
     * Verb plus object, so it reads as ending a mode rather than saving a form.
     *
     * Both the toolbar and a bare Escape finish through this action. Escape
     * dismisses open editors and temporary selections first (restored
     * 2026-10-20 in useCanvasKeyboard and useCaptureSession). The old Esc chip
     * and its `exitLabel` remain retired.
     *
     * Leaving commits every unsaved pin the user wrote in and preserves synced
     * ones (extension PinAnnotationCanvas.handleDone and
     * usePinSave.exitAnnotationMode; team review's create popover cleanup with
     * reason 'done-capturing', section 0460). A draft with nothing typed is
     * dropped (2026-10-24, open decision). The canvas unmounts so the page is
     * usable again.
     */
    done: 'Done capturing',
    /**
     * Suspends capture WITHOUT ending it, so the page underneath can be driven:
     * a modal closed, an SPA advanced to its next layer, a disclosure opened
     * over the thing the user wants to pin next.
     *
     * Deliberately not 'browse'. That word was `exitLabel`, the retired name for
     * where Escape landed AFTER capture mode ended, and reusing it here would
     * teach the retired idea back: this is the opposite claim, that the mode is
     * still on and every pin is still there.
     *
     * Verb plus object, matching `done`, and the pair reads as reversible: the
     * control that says "Pause capture" says "Resume capture" once pressed, so
     * the way back is the same button in the same place rather than something
     * the user has to go and find. Nothing is saved, exited or discarded either
     * way; only hit-testing changes.
     */
    pause: 'Pause capture',
    /** The way back from `pause`. Same control, so it cannot be missed. */
    resume: 'Resume capture',
    /** The select tool, named after the sidebar button that opens capture (IssueToolbar.tsx:158, captureButtonCopy.ts:5). */
    toolCreate: 'Create',
    createOptions: 'Create options',
    hoverInspector: 'Hover inspector',
    /** The same tool for guests, named after their sidebar button (0170-sidebar-main-ui-after-identity.js:18). */
    toolSelect: 'Select',
    toolScreenshot: 'Screenshot',
    /** The collapse chevron. Hides the tools, keeps Done, so a collapsed bar can still end the session. */
    hideTools: 'Hide tools',
    showTools: 'Show tools',
    /**
     * The pin-visibility eye on the toolbar, on both surfaces since 2026-10-19
     * (the extension's capture toolbar reads these from persistentPopupBody.tsx; team review App.tsx
     * reads them). Each surface appends its " · V" shortcut.
     */
    hideHighlights: 'Hide highlights on page',
    showHighlights: 'Show highlights on page',
    /** The caret beside the eye and the menu it opens (captureToolbarDom.ts, variant B). */
    highlightOptions: 'Highlight options',
    highlightsMenu: 'Highlights on page',
    layerPins: 'Pins',
    layerBoxes: 'Boxes',
    hideAll: 'Hide all',
    showAll: 'Show all',
    /** The Workflows menu button and its menu (captureToolbarDom.ts), named like the sidebar's Workflows tab (persistentPopupNav.ts). */
    workflows: 'Workflows',
};
/**
 * Copy for an issue row that exists but carries no user content yet. Every pin
 * is born in this state, because the immediate create-save persists a
 * placeholder before the user types (see pinUserContent.ts).
 *
 * Deliberately plain text, never a badge: this cue lands at the moment of the
 * user's FIRST successful capture, where an amber warning chip reads as a
 * scold instead of a next step (Peak-End).
 */
export const ISSUE_ROW_COPY = {
    /** States the action to take, not the deficiency the row has. */
    needsDetails: 'Add a title',
};
/**
 * The Record tool (docs/designs/recordings-on-issues.md, copy table approved by Emily 2026-10-25 on the
 * prototype in docs/prototypes/record-tool/). One term per concept: the tool is Record, what it makes is a
 * recording. Surfaces append their shortcut (" · R") to `tool`, as they do for the two tools above.
 */
export const RECORD_COPY = {
    tool: 'Record',
    /** Rendered as <kbd>Esc</kbd> followed by this; the area step says only how to leave it. */
    areaHintTail: 'to cancel',
    start: 'Start recording',
    stop: 'Stop recording',
    /** Shown while Chrome's share dialog is up: answers "is it recording my screen" (help chat, 2026-09-18). */
    sharing: 'Only this page is recorded, with no sound · Choose Share to start',
    /** The toolbar hint while recording is the timer alone. */
    timer: (elapsed, cap) => `${elapsed} of ${cap}`,
    stoppedTime: (capLabel) => `Stopped at the ${capLabel} limit.`,
    stoppedSize: 'Stopped at the 9 MB size limit.',
    stoppedHidden: 'Stopped when you switched tabs.',
    stoppedSharingEnded: 'Stopped when sharing ended.',
    wrongTab: 'Recording works on this tab only. Press Record and choose this tab.',
    noFrames: 'Recording could not start on this page. Try again, or use Screenshot.',
    uploadFailed: 'Could not upload the recording. Press Done to try again.',
    play: (filename) => `Play ${filename}`,
    playbackFailed: 'Could not play this recording here. Download it instead.',
    privacy: 'Only this page is recorded, with no sound.',
};
/** Decision 6 (2026-10-25): the full-size take, 2.5 Mbps, 30 s; the page stops itself at 9 MiB, under the 10 MiB comment limit. */
export const RECORD_LIMITS = { capMs: 30_000, sizeStopBytes: 9 * 1024 * 1024, bitsPerSecond: 2_500_000, frameRate: 30 };
/** recording-2026-10-25-09-42.mp4: readable in a tracker export, sortable, no spaces. */
export function recordingFileName(date, ext) {
    const pad = (n) => String(n).padStart(2, '0');
    return `recording-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${pad(date.getHours())}-${pad(date.getMinutes())}.${ext}`;
}
/** 0:12 from milliseconds, rounded to the second. */
export function formatRecordClock(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
//# sourceMappingURL=captureCopy.js.map